import {
  Nav,
  NavList,
  NavItem,
  Page,
  PageHeader,
  PageSidebar,
} from '@patternfly/react-core'
import * as React from 'react'
import {
  NavLink,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom'

import type { User } from '@app/state/user/state'
import { useDocumentTitle } from '@app/utils/use-document-title'

import type {
  RouteComponentProps} from 'react-router-dom';

interface IUserAppLayout {
  routes?: IAppRoute[]
  account?: User
  children?: React.ReactNode
}

export interface IAppRoute {
  label?: string
  component:
    | React.ComponentType<RouteComponentProps<unknown>>
    | React.ComponentType<unknown>
  exact?: boolean
  path: string
  title: string
  isAsync?: boolean
}

const RouteWithTitleUpdates = ({
  component: Component,
  title,
  ...rest
}: IAppRoute) => {
  useDocumentTitle(title)

  function routeWithTitle(routeProps: RouteComponentProps) {
    return <Component {...rest} {...routeProps} />
  }

  return <Route render={routeWithTitle} />
}

const UserLayout: React.FunctionComponent<IUserAppLayout> = ({
  routes,
  children,
}) => {
  const logoProps = {
    href: '/',
  }
  const [isNavOpen, setIsNavOpen] = React.useState(true)
  const [isMobileView, setIsMobileView] = React.useState(true)
  const [isNavOpenMobile, setIsNavOpenMobile] = React.useState(false)
  const onNavToggleMobile = () => {
    setIsNavOpenMobile(!isNavOpenMobile)
  }
  const onNavToggle = () => {
    setIsNavOpen(!isNavOpen)
  }
  const onPageResize = (props: { mobileView: boolean; windowSize: number }) => {
    setIsMobileView(props.mobileView)
  }

  const AppRoutes = () => (
    <Switch>
      {routes.map(({ path, exact, component, title, isAsync }, idx) => (
        <RouteWithTitleUpdates
          path={path}
          exact={exact}
          component={component}
          key={idx}
          title={title}
          isAsync={isAsync}
        />
      ))}
      <Route path="*">
        <Redirect to="/404" />
      </Route>
    </Switch>
  )

  const Header = (
    <PageHeader
      logo="intakeDI"
      logoProps={logoProps}
      showNavToggle
      isNavOpen={isNavOpen}
      onNavToggle={isMobileView ? onNavToggleMobile : onNavToggle}
    />
  )

  const Navigation = (
    <Nav id="nav-primary-simple" theme="dark">
      <NavList id="nav-list-simple">
        {routes.map(
          (route, idx) =>
            route.label && (
              <NavItem
                key={`${route.label}-${idx}`}
                id={`${route.label}-${idx}`}
              >
                <NavLink exact to={route.path} activeClassName="pf-m-current">
                  {route.label}
                </NavLink>
              </NavItem>
            ),
        )}
      </NavList>
    </Nav>
  )

  const Sidebar = (
    <PageSidebar
      theme="dark"
      nav={Navigation}
      isNavOpen={isMobileView ? isNavOpenMobile : isNavOpen}
    />
  )

  return (
    <Page
      mainContainerId="primary-app-container"
      header={Header}
      sidebar={Sidebar}
      onPageResize={onPageResize}
    >
      <AppRoutes />
      {children}
    </Page>
  )
}

export { UserLayout }
